import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API, formatDate } from "@/lib/api";
import axios from "axios";
import { CarFront, CalendarCheck, ImageIcon, Loader2, CheckCircle2, Wrench, PackageCheck, Star, MapPin, Phone } from "lucide-react";

const STATUS_COLORS = {
  "Beklemede": "bg-slate-100 text-slate-700 border-slate-200",
  "Devam Ediyor": "bg-amber-50 text-amber-800 border-amber-200",
  "Tamamlandı": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "Teslim Edildi": "bg-blue-50 text-blue-800 border-blue-200",
  "Kısmi Ödeme": "bg-violet-50 text-violet-800 border-violet-200",
  "Tahsil Edildi": "bg-emerald-50 text-emerald-800 border-emerald-200",
};

const STATUS_STEPS = [
  { key: "Beklemede", label: "Kabul Edildi", icon: PackageCheck },
  { key: "Devam Ediyor", label: "İşlem Devam Ediyor", icon: Wrench },
  { key: "Tamamlandı", label: "Tamamlandı", icon: CheckCircle2 },
  { key: "Teslim Edildi", label: "Teslim Edildi", icon: CarFront },
];

export default function TrackJob() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [business, setBusiness] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${API}/public/track/${token}`),
      axios.get(`${API}/public/business-info`).catch(() => ({ data: {} })),
    ])
      .then(([r, b]) => {
        if (mounted) {
          setData(r.data);
          setBusiness(b.data || {});
        }
      })
      .catch((e) => {
        if (mounted) setErr(e?.response?.data?.detail || "Kayıt bulunamadı.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-8 text-center shadow-sm">
          <p className="font-display text-lg font-bold text-slate-900">Kayıt Bulunamadı</p>
          <p className="text-sm text-slate-600 mt-2">{err || "Bu link geçersiz veya süresi dolmuş olabilir."}</p>
          <p className="text-xs text-slate-500 mt-6">Global Car Service · Kaporta & Boya Atölyesi</p>
        </div>
      </div>
    );
  }

  const beforePhotos = (data.photos || []).filter((p) => p.kind === "before");
  const afterPhotos = (data.photos || []).filter((p) => p.kind === "after");
  const statusIndex = STATUS_STEPS.findIndex((s) => s.key === data.status);
  const isFinished = data.status === "Tamamlandı" || data.status === "Teslim Edildi" || data.status === "Tahsil Edildi";
  const businessName = business?.business_name || "Global Car Service";

  return (
    <div data-testid="track-page" className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-4 py-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center">
            <CarFront className="w-6 h-6" strokeWidth={2.2} />
          </div>
          <div>
            <p className="font-display text-lg font-bold leading-tight">{businessName}</p>
            <p className="text-[11px] uppercase tracking-widest text-white/60 font-semibold">Aracınızın Durumu</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-6 pb-16">
        {/* Vehicle Info Card */}
        <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Sayın</p>
              <p className="font-display text-xl font-bold text-slate-900">{data.customer_name || "Müşterimiz"}</p>
              {data.plate && (
                <p className="mt-1 inline-block bg-slate-900 text-white font-mono-num font-bold text-sm px-3 py-1 rounded">
                  {data.plate}
                </p>
              )}
            </div>
            <span
              data-testid="track-status-badge"
              className={`inline-block px-3 py-1.5 rounded-full text-xs font-semibold border ${STATUS_COLORS[data.status] || "bg-slate-100 text-slate-700 border-slate-200"}`}
            >
              {data.status}
            </span>
          </div>
          {data.job_type && (
            <p className="mt-3 text-sm text-slate-700">
              <b>İşlem:</b> {data.job_type}
            </p>
          )}
          {data.description && (
            <p className="mt-1 text-sm text-slate-600 whitespace-pre-line">{data.description}</p>
          )}
          {data.expected_delivery && (
            <div className="mt-3 flex items-center gap-2 text-sm text-blue-800 bg-blue-50 border border-blue-200 rounded p-2">
              <CalendarCheck className="w-4 h-4" />
              <span>Tahmini Teslim: <b>{formatDate(data.expected_delivery)}</b></span>
            </div>
          )}
        </section>

        {/* Timeline */}
        <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
          <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-4">Süreç</p>
          <ol className="space-y-3">
            {STATUS_STEPS.map((step, i) => {
              const done = i <= statusIndex;
              const active = i === statusIndex;
              const Icon = step.icon;
              return (
                <li
                  key={step.key}
                  data-testid={`track-step-${step.key}`}
                  className={`flex items-center gap-3 ${done ? "" : "opacity-40"}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                    done ? (active ? "bg-slate-900 text-white ring-4 ring-slate-900/10" : "bg-emerald-500 text-white") : "bg-slate-200 text-slate-500"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-semibold ${done ? "text-slate-900" : "text-slate-500"}`}>
                      {step.label}
                    </p>
                  </div>
                  {done && !active && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                </li>
              );
            })}
          </ol>
        </section>

        {/* Photos */}
        {(beforePhotos.length > 0 || afterPhotos.length > 0) && (
          <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <ImageIcon className="w-4 h-4 text-slate-500" />
              <p className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Fotoğraflar</p>
            </div>

            {beforePhotos.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-semibold text-slate-900 mb-2">Öncesi</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {beforePhotos.map((p) => (
                    <a
                      key={p.id}
                      href={`${API}/public/track/${token}/photo/${p.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aspect-square border border-slate-200 rounded-md overflow-hidden bg-slate-100 hover:opacity-90"
                    >
                      <img
                        src={`${API}/public/track/${token}/photo/${p.id}`}
                        alt="Öncesi"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}

            {afterPhotos.length > 0 && (
              <div>
                <p className="text-sm font-semibold text-slate-900 mb-2">Sonrası</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {afterPhotos.map((p) => (
                    <a
                      key={p.id}
                      href={`${API}/public/track/${token}/photo/${p.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aspect-square border border-slate-200 rounded-md overflow-hidden bg-slate-100 hover:opacity-90"
                    >
                      <img
                        src={`${API}/public/track/${token}/photo/${p.id}`}
                        alt="Sonrası"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Google Review CTA — sadece iş bitince gösterilir */}
        {isFinished && business?.google_review_url && (
          <section
            data-testid="google-review-cta"
            className="bg-white border-2 border-amber-300 rounded-lg p-6 shadow-sm relative overflow-hidden"
          >
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-amber-100 rounded-full opacity-50" />
            <div className="relative">
              <div className="flex items-center gap-1 mb-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                ))}
              </div>
              <p className="font-display text-lg font-bold text-slate-900">Deneyiminizi nasıl buldunuz?</p>
              <p className="text-sm text-slate-600 mt-1">
                Memnun kaldıysanız Google'da <b>{businessName}</b>'e yorum bırakmanız bize çok yardımcı olur.
              </p>
              <a
                data-testid="google-review-btn"
                href={business.google_review_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-md bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold shadow-sm transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Google'da Yorum Bırak
              </a>
            </div>
          </section>
        )}

        <footer className="text-center text-xs text-slate-500 pt-4">
          <p className="font-semibold text-slate-700">{businessName}</p>
          {business?.address && (
            <p className="mt-1 flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" /> {business.address}
            </p>
          )}
          {business?.phone && (
            <p className="mt-1 flex items-center justify-center gap-1">
              <Phone className="w-3 h-3" /> {business.phone}
            </p>
          )}
          {business?.google_maps_url && (
            <a
              href={business.google_maps_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-block text-blue-600 hover:underline"
            >
              Google Maps'te Bul
            </a>
          )}
          <p className="mt-3 text-slate-400">Kaporta & Boya Atölyesi</p>
        </footer>
      </main>
    </div>
  );
}
